import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import { VehicleCardBackground } from '@allesauto/utils/consts';
import { Popover, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import VehicleCard from '../latestVehiclesTeaser/vehicleCard/VehicleCard';
import LoaderOverlay from '../loaderOverlay';
import NextLink from '../nextLink';

export interface ITeaserInformation {
  headline: string;
  condition: string;
  background: string;
  imageUrl: string;
  hasImageFullscreen: boolean;
  link: string;
}

export interface INavigationMenu {
  id: string;
  name: string;
  sections: {
    id: string;
    name: string;
    items: { name: string; href: string }[];
  }[];
  teaserInformation: ITeaserInformation[];
}

export interface HeaderMenuProps {
  open: boolean;
  handleHover: (flag: boolean) => void;
  navigation?: INavigationMenu[];
  className: string;
}

const HeaderMenu: FC<HeaderMenuProps> = ({
  navigation,
  open,
  handleHover,
  className: popoverGroupClass,
}) => {
  const [hoveredFilter, setHoveredFilter] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const filteredItems =
    navigation && navigation.find((nav) => nav.id === hoveredFilter);
  const hasTwoTeasers = useMemo(
    () => filteredItems?.teaserInformation.length === 2,
    [filteredItems]
  );
  const menuHeight = 500;

  const router = useRouter();

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      setLoading(false);
    };

    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router]);

  const pushToDataLayer = (itemName: string) => {
    // dataLayer push after click on navigation
    pushAnalyticsEvent({
      event: 'navigation',
      navigation_category: 'main',
      navigation_item: itemName.toLowerCase(),
    });
  };

  const linkHandler = useCallback(
    (linkName: string) => () => {
      setLoading(true);
      pushToDataLayer(linkName);
    },
    []
  );

  const className = {
    popoverClass: 'flex h-full',
    buttonContainerClass: 'flex pr-40px relative',
    popoverButtonClass:
      'border-b-7px duration-200 ease-out flex font-normal font-regular items-center pt-px relative text-p1 text-purple80 monochrome:text-black transition-colors z-20 cursor-pointer',
    popoverPanelClass:
      'absolute top-[69px] left-2/4 translate-x-[-50%] w-full bg-grey90',
    gridClass: clsx(
      `grid grid-cols-24 h-[500px] max-w-screen-xl ml-auto mr-auto relative text-center top-0`
    ),
    sectionTitleClass:
      'text-left font-regular font-weight-bold pb-40px pt-[60px] text-green60 monochrome:text-white text-p2 overflow-hidden text-ellipsis whitespace-nowrap block wl-text-brand',
    linkClass:
      'text-left duration-300 font-regular no-underline text-p1 text-white transition-colors hover:text-grey50 overflow-hidden text-ellipsis whitespace-break-spaces block monochrome:text-white monochrome:hover:text-grey50',
  };

  return (
    <Popover.Group
      onMouseLeave={() => {
        !loading && handleHover(false);
      }}
      className={popoverGroupClass}
    >
      {navigation &&
        navigation.map((category) => (
          <Popover key={category.name} className={className.popoverClass}>
            <>
              <div className={className.buttonContainerClass}>
                <div
                  className={clsx(
                    hoveredFilter === category.id && open
                      ? 'border-green70 monochrome:border-grey70 wl-border-highlight'
                      : 'border-transparent hover:border-green70 monochrome:hover:border-grey70 wl-border-highlight--hover hover:border-b-7px',
                    className.popoverButtonClass
                  )}
                  onKeyPress={() => {
                    setHoveredFilter(category.id);
                    handleHover(true);
                  }}
                  tabIndex={0}
                  onMouseEnter={() => {
                    setHoveredFilter(category.id);
                    handleHover(true);
                  }}
                >
                  {category.name}
                </div>
              </div>

              <Popover.Panel static className={className.popoverPanelClass}>
                <Transition
                  enter="transition-[max-height] duration-250 ease"
                  enterFrom="!max-h-0 overflow-hidden"
                  enterTo="overflow-hidden max-h-[500px]"
                  leave="transition-[max-height] duration-100 ease"
                  leaveFrom="overflow-hidden max-h-[500px]"
                  leaveTo="!max-h-0 overflow-hidden"
                  show={open}
                >
                  {loading && (
                    <LoaderOverlay
                      customClassName={{
                        containerCustomClass: '!bg-opacity-0',
                        loaderCustomClass: `!top-[calc((100vh+500px)/2-50px)]`,
                      }}
                    />
                  )}
                  <div className={className.gridClass}>
                    {filteredItems && (
                      <>
                        <div
                          className={clsx(
                            hasTwoTeasers ? 'lg:col-end-10' : 'lg:col-end-18',
                            'flex lg:col-start-2'
                          )}
                        >
                          {filteredItems.sections.map((section, i) => (
                            <div
                              key={section.id + i}
                              className={clsx(
                                hasTwoTeasers ? 'w-1/2' : 'w-1/4',
                                'lg:pr-20px'
                              )}
                            >
                              <p className={className.sectionTitleClass}>
                                {section.name}
                              </p>
                              <ul>
                                {section.items.map((item, i) => (
                                  <li
                                    key={category.id + section.id + i}
                                    className={clsx(
                                      i !== section.items.length - 1
                                        ? 'pb-20px'
                                        : 'pb-0',
                                      'relative'
                                    )}
                                  >
                                    {/* TODO: Remove this workaround after filters are fixed */}
                                    {section.name === 'Unsere Fahrzeuge' ? (
                                      <a
                                        href={item.href}
                                        tabIndex={0}
                                        className={className.linkClass}
                                        onClick={linkHandler(item.name)}
                                      >
                                        {item.name}
                                      </a>
                                    ) : (
                                      <NextLink
                                        href={item.href}
                                        tabIndex={0}
                                        className={className.linkClass}
                                        onClick={linkHandler(item.name)}
                                      >
                                        {item.name}
                                      </NextLink>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                        <div
                          className={clsx(
                            hasTwoTeasers
                              ? 'lg:col-start-13'
                              : 'lg:col-start-19',
                            'flex text-left lg:col-end-25'
                          )}
                        >
                          {filteredItems.teaserInformation.map(
                            (teaserInfo, index) => (
                              <VehicleCard
                                key={index}
                                {...teaserInfo}
                                background={
                                  teaserInfo.background as VehicleCardBackground
                                }
                                fixedHeightValue={menuHeight}
                                imageObjectFit={'cover'}
                              />
                            )
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </Transition>
              </Popover.Panel>
            </>
          </Popover>
        ))}
    </Popover.Group>
  );
};

export default HeaderMenu;
